// TreemapView — nested rectangles sized by subtree leaf count.
//
// Each node becomes a rectangle. Internal nodes carry a short header
// bar and contain their reports as sub-rectangles; leaves render as
// filled tiles. Layout uses slice-and-dice (alternating axis with the
// longer dimension), weighted by leaf count, so the visual area of
// each rectangle reads as "size of this team".
//
// Best at answering "where are the people?" — weakest at showing
// individual reporting lines.
import React, { useMemo, useState } from "react";
import { childrenOf, colorFor, nameOf, nodeMatches, withSyntheticRoot } from "./shared";

const W           = 880;
const H           = 560;
const HEADER      = 16;
const MIN_TILE    = 4;
const PAD_OUTER   = 8;

function leafCount(n) {
  const kids = childrenOf(n);
  if (kids.length === 0) return 1;
  return kids.reduce((acc, c) => acc + leafCount(c), 0);
}

// Recursively assign {x, y, w, h, depth} to every node.
function layout(node, rect, depth) {
  node._rect = { ...rect, depth };
  const kids = childrenOf(node);
  if (kids.length === 0) return;

  const innerX = rect.x + 1;
  const innerY = rect.y + HEADER;
  const innerW = rect.w - 2;
  const innerH = rect.h - HEADER - 1;
  if (innerW < MIN_TILE || innerH < MIN_TILE) return;

  const horizontal = innerW >= innerH;
  const total      = kids.reduce((s, c) => s + leafCount(c), 0) || 1;
  let cursor       = horizontal ? innerX : innerY;
  kids.forEach((c) => {
    const frac = leafCount(c) / total;
    const size = horizontal ? innerW * frac : innerH * frac;
    const r = horizontal
      ? { x: cursor, y: innerY, w: size, h: innerH }
      : { x: innerX, y: cursor, w: innerW, h: size };
    layout(c, r, depth + 1);
    cursor += size;
  });
}

function flatten(node, out) {
  out.push(node);
  childrenOf(node).forEach((c) => flatten(c, out));
}

export default function TreemapView({ tree, search, onSelectUser }) {
  const [hover, setHover] = useState(null);

  const { nodes, matchKey } = useMemo(() => {
    const root = withSyntheticRoot(tree);
    layout(root, { x: PAD_OUTER, y: PAD_OUTER, w: W - 2 * PAD_OUTER, h: H - 2 * PAD_OUTER }, 0);
    const flat = [];
    flatten(root, flat);

    // Pre-compute match set so the render dim-out is O(1) per node.
    const matches = new Set();
    if (search) {
      const collect = (n) => {
        if (nodeMatches(n, search) && !n.__synthetic) matches.add(n.id || nameOf(n));
        childrenOf(n).forEach(collect);
      };
      collect(root);
    }
    return { nodes: flat, matchKey: matches };
  }, [tree, search]);

  return (
    <div className="p-4 overflow-auto max-h-[600px]">
      <div style={{ position: "relative", width: W, margin: "0 auto" }}>
        <svg width={W} height={H} style={{ display: "block" }}>
          {nodes.map((n, i) => {
            const r = n._rect;
            if (!r || r.w < MIN_TILE || r.h < MIN_TILE) return null;
            const color   = colorFor(n.role);
            const hasKids = childrenOf(n).length > 0;
            const isMatch = !search || matchKey.has(n.id || nameOf(n));
            const dim     = search && !isMatch;
            // Internal nodes: hollow rect with a header band. Leaves: filled.
            const fill   = hasKids ? "transparent"   : `${color}33`;
            const stroke = hasKids ? `${color}55`    : `${color}aa`;

            const showLabel = r.w > 60 && r.h > (hasKids ? 18 : 24);

            return (
              <g
                key={n.id || `t${i}`}
                style={{ cursor: n.__synthetic ? "default" : "pointer", opacity: dim ? 0.18 : 1 }}
                onClick={() => !n.__synthetic && onSelectUser?.(n)}
                onMouseEnter={() => !n.__synthetic && setHover({ node: n, depth: r.depth })}
                onMouseLeave={() => setHover(null)}
              >
                <rect
                  x={r.x} y={r.y}
                  width={Math.max(0, r.w - 1)}
                  height={Math.max(0, r.h - 1)}
                  rx={hasKids ? 6 : 3}
                  ry={hasKids ? 6 : 3}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={hasKids ? 1 : 0.6}
                />
                {hasKids && (
                  <rect
                    x={r.x} y={r.y}
                    width={Math.max(0, r.w - 1)} height={HEADER}
                    rx={6} ry={6}
                    fill={`${color}22`}
                  />
                )}
                {showLabel && hasKids && (
                  <text x={r.x + 6} y={r.y + 11} fill="#fff" fontSize={10} fontWeight={600}>
                    {truncate(nameOf(n), Math.floor((r.w - 10) / 6))}
                  </text>
                )}
                {showLabel && !hasKids && (
                  <>
                    <text x={r.x + 6} y={r.y + 16} fill="#fff" fontSize={11} fontWeight={600}>
                      {truncate(nameOf(n), Math.floor((r.w - 10) / 6))}
                    </text>
                    {r.h > 36 && (
                      <text x={r.x + 6} y={r.y + 30} fill={color} fontSize={9}>
                        {truncate(n.title || n.designation || n.role || "", Math.floor((r.w - 10) / 5))}
                      </text>
                    )}
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {hover && (
          <div
            style={{
              position: "absolute", top: 8, right: 8,
              background: "#0a0a0a", border: "1px solid #1c1c1c",
              borderRadius: 8, padding: "8px 12px",
              fontSize: 11, color: "#fff", pointerEvents: "none",
              minWidth: 180, maxWidth: 240,
            }}
          >
            <div style={{ fontWeight: 600 }}>{nameOf(hover.node)}</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 2 }}>
              {hover.node.title || hover.node.designation || "—"}
            </div>
            <div style={{ color: colorFor(hover.node.role), fontSize: 10, marginTop: 2 }}>
              {hover.node.role || "staff"}
              {hover.node.department ? ` · ${hover.node.department}` : ""}
            </div>
            {childrenOf(hover.node).length > 0 && (
              <div style={{ color: "#666", fontSize: 10, marginTop: 4 }}>
                {leafCount(hover.node)} people in subtree
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function truncate(s, n) {
  const str = String(s ?? "");
  if (n < 3) return "";
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}
